import {TooltipItemType, TooltipRow} from 'common/components/chart-tooltip/types';
import {commaizeNumber} from 'common/util';
import {TextLabelFormatter} from 'common/report/funnels/models/bar-chart/types';
import {TEXT_HIGHLIGHT_THEME} from 'common/components/text-highlight';
import {LiftType} from 'common/report/util/lift/types';

import {FrequencyViewType} from './types';

export function getBucketLabel(bucketStart: number, bucketEnd?: number) {
  return bucketEnd && bucketEnd - bucketStart > 1 ? `${bucketStart} - ${bucketEnd}` : `${bucketStart}`;
}

const pastTenseActions: Record<FrequencyViewType, string> = {
  [FrequencyViewType.Conversion]: `converted`,
  [FrequencyViewType.ConversionRate]: `converted`,
  [FrequencyViewType.Dropoff]: `dropped off`,
};

export function getToolTipText(
  count: number,
  percentage: number,
  bucketLabel: string,
  plural: boolean,
  stepName: string,
  viewType: FrequencyViewType,
): TooltipRow[] {
  const bucketUnit = plural ? `times` : `time`;
  const pastTenseAction = pastTenseActions[viewType];
  return [
    {
      items: [{text: `For users who did`, type: TooltipItemType.Text}],
    },
    {
      items: [
        // $stepName $x times,
        {text: stepName, type: TooltipItemType.Data},
        {text: bucketLabel, type: TooltipItemType.Data},
        {text: `${bucketUnit},`, type: TooltipItemType.Text},
      ],
    },
    {
      items: [
        // 45 (22%) of those users dropped off
        {text: commaizeNumber(count), type: TooltipItemType.Data},
        {text: `(${percentage}%)`, type: TooltipItemType.Data},
        {text: `of those users ${pastTenseAction}.`, type: TooltipItemType.Text},
      ],
    },
  ];
}

export function getLiftToolTipText({
  count,
  percentage,
  liftComparisonCount,
  bucketLabel,
  segmentName,
  baselineSegmentName,
  plural,
  stepName,
  liftType,
  dateRanges,
}: {
  count: number;
  percentage: number;
  liftComparisonCount: number;
  bucketLabel: string;
  segmentName: string;
  baselineSegmentName: string;
  plural: boolean;
  stepName: string;
  liftType: LiftType;
  dateRanges?: string[];
}): TooltipRow[] {
  const bucketUnit = plural ? `times` : `time`;

  let liftValueTheme: TEXT_HIGHLIGHT_THEME = TEXT_HIGHLIGHT_THEME.NEUTRAL;
  if (liftType !== LiftType.Overall) {
    if (percentage > 0) {
      liftValueTheme = TEXT_HIGHLIGHT_THEME.POSITIVE;
    } else if (percentage < 0) {
      liftValueTheme = TEXT_HIGHLIGHT_THEME.NEGATIVE;
    }
  }

  let primaryMetricName = segmentName;
  let compareMetricName = baselineSegmentName;
  if (liftType === LiftType.Time) {
    primaryMetricName = dateRanges[0];
    compareMetricName = dateRanges[1];
  } else if (liftType === LiftType.Overall) {
    compareMetricName = `Overall`;
  }

  return [
    {
      items: [
        // $stepName $x times,
        {text: stepName, type: TooltipItemType.Data},
        {text: bucketLabel, type: TooltipItemType.Data},
        {text: bucketUnit, type: TooltipItemType.Text},
      ],
    },
    {
      // x%
      items: [{text: `${percentage}%`, type: TooltipItemType.Data, theme: liftValueTheme}],
    },
    {
      items: [
        // count of this segment + segment label
        {text: commaizeNumber(count), type: TooltipItemType.Data},
        {text: primaryMetricName, type: TooltipItemType.Text},
      ],
    },
    {
      items: [
        // count of baseline + baseline label (segment or "Overall")
        {text: commaizeNumber(liftComparisonCount), type: TooltipItemType.Data},
        {text: compareMetricName, type: TooltipItemType.Text},
      ],
    },
  ];
}

export function getMedianToolTipText(median: string): TooltipRow[] {
  return [
    {
      items: [
        {text: median, type: TooltipItemType.Data},
        {text: `median events`, type: TooltipItemType.Text},
      ],
    },
  ];
}

export function getMedianFormatter(median: number) {
  return `${median ? median : `N/A`}`;
}

export const partialFormatter: TextLabelFormatter = {
  getBucketLabel,
  getMedianFormatter,
  getMedianToolTipText,
};

export enum FunnelStepBucketAccessors {
  DropoffStepFreqBuckets = `dropoff_step_freq_buckets`,
  DropoffStepFreqPercentiles = `dropoff_step_freq_percentiles`,
  ConvertStepFreqBuckets = `convert_step_freq_buckets`,
  ConvertStepFreqPercentiles = `convert_step_freq_percentiles`,
  ConvertRateFreqBuckets = `convert_rate_freq_buckets`,
  ConvertRateFreqPercentiles = `convert_rate_freq_percentiles`,
}

// Wrapper to more neatly toggle between strings based on conversion type
export function conversionMode(type = FrequencyViewType.Conversion) {
  switch (type) {
    case FrequencyViewType.Conversion:
      return {
        bucketAccessor: FunnelStepBucketAccessors.ConvertStepFreqBuckets,
        medianAccessor: FunnelStepBucketAccessors.ConvertStepFreqPercentiles,
        representationText: `conversions`,
      } as const;
    case FrequencyViewType.ConversionRate:
      return {
        bucketAccessor: FunnelStepBucketAccessors.ConvertRateFreqBuckets,
        medianAccessor: FunnelStepBucketAccessors.ConvertRateFreqPercentiles,
        representationText: `conversion rate`,
      } as const;
    default:
      return {
        bucketAccessor: FunnelStepBucketAccessors.DropoffStepFreqBuckets,
        medianAccessor: FunnelStepBucketAccessors.DropoffStepFreqPercentiles,
        representationText: `drop-offs`,
      } as const;
  }
}
